import React, { useEffect, useRef, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/utils/api";

const BASE = "/v2/payment-switch";
const LABEL = { wallet: "Wallet (prepaid)", credit: "Credit", cash: "Cash", ppp_cash: "Pay Post Pickup - cash", ppp_upi: "Pay Post Pickup - UPI link" };
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function effect(o) {
  const out = [];
  if (o.reverse_cash) out.push(`reverses the ${inr(o.reverse_cash)} cash receipt`);
  if (o.refund_wallet) out.push(`refunds ${inr(o.refund_wallet)} to the customer's wallet`);
  if (o.debit_wallet) out.push(`charges ${inr(o.debit_wallet)} to the wallet`);
  if (o.record_cash) out.push(`records ${inr(o.record_cash)} cash received`);
  out.push(o.new_status === "paid" ? "marks the order paid" : "leaves the order payment pending");
  return out.join(", ");
}

/** Change how a shipment is paid (wallet / credit / cash / pay-post-pickup) and move the money to match. */
export default function PaymentSwitchDialog({ shipment, onClose, onDone }) {
  const [st, setSt] = useState(null);
  const [to, setTo] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    let live = true;
    api.get(`${BASE}/${shipment.id}`)
      .then((r) => { if (live) setSt(r.data); })
      .catch((e) => { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Could not load payment details"); closeRef.current(); });
    return () => { live = false; };
  }, [shipment.id]);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post(`${BASE}/${shipment.id}`, { to, reason });
      toast.success(`Payment changed to ${LABEL[to]}${r.data.refunded_to_wallet ? ` - ${inr(r.data.refunded_to_wallet)} refunded to wallet` : ""}`);
      await onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(d && typeof d === "object" ? d : { title: "Payment not changed", message: d || "Something went wrong - please try again." });
    }
    setBusy(false);
  };

  const chosen = st?.options?.find((o) => o.to === to);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="payment-switch-dialog">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-bold text-slate-800">Change payment - {shipment.order_id}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        {!st ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <div className="p-4 space-y-3">
            <div className="text-xs bg-slate-50 border rounded-lg p-3 space-y-0.5">
              <div>Now: <b>{LABEL[st.mode] || st.mode}</b> &middot; {st.payment_status === "paid" ? "Paid" : "Payment pending"} &middot; <b>{inr(st.amount)}</b></div>
              {st.customer?.name && <div>Customer: {st.customer.name} &middot; wallet {inr(st.customer.wallet_balance)}{st.customer.credit_enabled ? ` (credit limit ${inr(st.customer.credit_limit)})` : ""}</div>}
              {st.wallet_held > 0 && <div>Wallet is currently charged <b>{inr(st.wallet_held)}</b> for this order.</div>}
              {st.cash_held > 0 && <div>Cash received on record: <b>{inr(st.cash_held)}</b>.</div>}
              {!st.connected && <div className="text-amber-700">Not connected to a carrier yet.</div>}
            </div>
            <p className="text-xs font-semibold text-slate-600">Change to:</p>
            {st.options.map((o) => (
              <label key={o.to} className={`block border rounded-lg px-3 py-2 text-sm ${!o.allowed ? "opacity-50" : "cursor-pointer"} ${to === o.to ? "border-brand-500 bg-brand-50" : ""}`}>
                <div className="flex items-center gap-2">
                  <input type="radio" name="pay-to" disabled={!o.allowed} checked={to === o.to} onChange={() => setTo(o.to)} data-testid={`pay-to-${o.to}`} />
                  <span className="font-semibold">{LABEL[o.to]}</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-1 ml-6">{o.allowed ? `${effect(o)}.${o.notes?.length ? " " + o.notes.join(" ") : ""}` : o.reason}</div>
              </label>
            ))}
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={200} placeholder="Reason (required) - e.g. customer paid cash at counter"
              className="w-full border rounded-lg px-2.5 py-1.5 text-sm" data-testid="pay-switch-reason" />
            {chosen && <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">This will move real money in the wallet/cash records and is saved in the audit log.</p>}
            {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2"><b>{err.title}</b><div>{err.message}</div></div>}
          </div>
        )}
        <div className="flex gap-2 justify-end p-4 border-t">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border rounded-lg">Close</button>
          <button onClick={submit} disabled={!to || reason.trim().length < 3 || busy} data-testid="pay-switch-confirm"
            className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-brand-500 text-white disabled:opacity-50 flex items-center gap-1">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Change payment
          </button>
        </div>
      </div>
    </div>
  );
}
